<template>
  <div class="area">
    <div v-if="!loading && !data.length" class="center">
      <el-icon>
        <WalletFilledElementIcon />
      </el-icon>
      No data on advertising spending
    </div>
    <div
      v-else
      ref="chartdiv"
      v-loading="loading"
      element-loading-text="Activity loading"
      element-loading-background="transparent"
      style="height: 100%"
    />
  </div>
</template>

<script lang="ts">
import useResize from '@/hooks/useResize'
import { AdType } from '@/api/types'

import {
  defineComponent,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  shallowRef,
  nextTick
} from 'vue'

import { am4core, am4charts } from '@/utils/async_chart_loader'
import { formatMoney } from '../utils/formatters'

const COLORS: Record<number, string> = {
  1: '#ff8788',
  2: '#ffc84a',
  10: '#1890ff',
  9: '#a9a9a9',
  6: '#59db8e',
  [AdType.Tab]: '#b252d4'
}

export default defineComponent({
  name: 'ActivityChart',
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: true
    }
  },
  setup(props, context) {
    const { isDesktop } = useResize()
    const chartdiv = ref(null)
    const chartBox = shallowRef()
    let renderVersion = 0

    const renderChart = async () => {
      const currentRender = ++renderVersion
      clearChart()
      if (props.data.length < 1) {
        return
      }

      await nextTick()
      if (currentRender !== renderVersion || !chartdiv.value) {
        return
      }

      const chart = am4core.create(chartdiv.value, am4charts.XYChart)
      chart.padding(10, 90, 0, 0)

      const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
      categoryAxis.renderer.grid.template.location = 0
      categoryAxis.dataFields.category = 'title'
      categoryAxis.renderer.minGridDistance = 1
      categoryAxis.renderer.inversed = true
      categoryAxis.renderer.grid.template.disabled = true

      const valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
      valueAxis.min = 0
      valueAxis.extraMax = 0.25

      const series = chart.series.push(new am4charts.ColumnSeries())
      series.dataFields.categoryY = 'title'
      series.dataFields.valueX = 'spends'
      series.dataFields.value = 'type_id'
      series.tooltipText = '{valueX.value}'
      series.columns.template.strokeOpacity = 0
      series.columns.template.column.cornerRadiusBottomRight = 5
      series.columns.template.column.cornerRadiusTopRight = 5

      const labelBullet = series.bullets.push(new am4charts.LabelBullet())
      labelBullet.label.horizontalCenter = 'left'
      labelBullet.label.text = '{valueX}'
      labelBullet.locationX = 1
      labelBullet.label.dx = 6
      labelBullet.label.truncate = false
      labelBullet.label.hideOversized = false
      labelBullet.label.adapter.add('text', (_text: string, target: any) => {
        return formatMoney(target.dataItem?.valueX || 0, { currency: true })
      })
      // as by default columns of the same series are of the same color, we add adapter which takes colors from chart.colors color set
      series.columns.template.adapter.add(
        'fill',
        function (fill: any, target: any) {
          return am4core.color(COLORS[target.dataItem.value])
        }
      )

      categoryAxis.sortBySeries = series
      chart.data = props.data
      chartBox.value = chart
    }

    const clearChart = () => {
      if (chartBox.value && !chartBox.value.isDisposed) {
        chartBox.value.dispose()
        chartBox.value = null
      }
    }

    onMounted(() => {
      renderChart()
    })

    onBeforeUnmount(() => {
      clearChart()
    })

    watch(
      (_: any) => props.data,
      (_: any) => renderChart()
    )

    return { isDesktop, chartdiv }
  }
})
</script>

<style lang="scss" scoped>
.area {
  height: 285px !important;
  position: relative;
  background-color: var(--el-color-white);
  margin-left: 5px;

  .center {
    position: absolute;
    margin: 0;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }

  :deep(svg) {
    font-size: 0.8em;
  }
}
</style>
